import CLIENT from '../utils/restClient';
import Logger, { LOG_LEVELS } from '../utils/logger';
import LANGUAGES_CONTROLLER from '../controllers/languages';

export const MODULE_NAME = 'DYNAMIC_REPORTS_CONTROLLER';
const log = /** @type {{debug: function}} */ new Logger(MODULE_NAME, LOG_LEVELS.DEBUG);

function namespaced(name) {
    return MODULE_NAME + '.' + name;
}

const LOAD_REPORT_SPEC = namespaced('LOAD_REPORT_SPEC');
const LOAD_REPORT_SPEC_OK = namespaced('LOAD_REPORT_SPEC_OK');
const LOAD_REPORT_SPEC_FAIL = namespaced('LOAD_REPORT_SPEC_FAIL');
const LOAD_REPORT_DATA = namespaced('LOAD_REPORT_DATA');
const LOAD_REPORT_DATA_OK = namespaced('LOAD_REPORT_DATA_OK');
const LOAD_REPORT_DATA_FAIL = namespaced('LOAD_REPORT_DATA_FAIL');
const EXPORT_ALL = namespaced('EXPORT_ALL');
const EXPORT_ALL_OK = namespaced('EXPORT_ALL_OK');
const EXPORT_ALL_FAIL = namespaced('EXPORT_ALL_FAIL');

const ACTION_TYPES = {
    LOAD_REPORT_SPEC,
    LOAD_REPORT_SPEC_OK,
    LOAD_REPORT_SPEC_FAIL,
    LOAD_REPORT_DATA,
    LOAD_REPORT_DATA_OK,
    LOAD_REPORT_DATA_FAIL,
    EXPORT_ALL,
    EXPORT_ALL_OK,
    EXPORT_ALL_FAIL
}

const initialState = {
    specifications: {},
    reportsData: null,
    error: null,
    loadingSpec: false,
    loadingData: false,
    loading: false
}

const updateSubStateFromAction = (state = initialState, action) => {
    switch (action.type) {
        case LOAD_REPORT_SPEC:
            return {
                ...state,
                loadingSpec: true,
                loading: true,
                error: null
            };
        case LOAD_REPORT_DATA:
            return {
                ...state,
                loadingData: true,
                loading: true,
                error: null
            };
        case LOAD_REPORT_SPEC_OK:
            return {
                ...state,
                specifications: action.payload,
                loadingSpec: false,
                loading: state.loadingData,
                error: null
            };
        case LOAD_REPORT_DATA_OK:
            return {
                ...state,
                reportsData: action.payload,
                loadingData: false,
                loading: state.loadingSpec,
                error: null
            }
        case LOAD_REPORT_SPEC_FAIL:
            return {
                ...state,
                loadingSpec: false,
                loading: state.loadingData,
                error: action.payload,
                reportsData: null
            };
        case LOAD_REPORT_DATA_FAIL:
            return {
                ...state,
                loadingData: false,
                loading: state.loadingSpec,
                error: action.payload,
                reportsData: null
            };
        case EXPORT_ALL:
            return {
                ...state,
                showToast: false,
                error: null
            };
        case EXPORT_ALL_OK:
            return {
                ...state,
                showToast: true,
                error: null,
                exportResponse: action.payload
            };
        case EXPORT_ALL_FAIL:
            return {
                ...state,
                showToast: false,
                error: action.error
            };
        default:
            return state;
    }
}

const API = {
    doLoadReportSpecification: (enabledAdditionalLogging) => {
        return (dispatch) => {
            log.debug('doLoadReportSpecification(): featureFlag: ', enabledAdditionalLogging);
            dispatch({ type: LOAD_REPORT_SPEC });
            return CLIENT.getReportSpecification(enabledAdditionalLogging)
                .then((response) => {
                    log.debug('doLoadReportSpecification(): success: ', response);
                    dispatch({ type: LOAD_REPORT_SPEC_OK, payload: response });
                })
                .catch((error) => {
                    log.debug('doLoadReportSpecification(): failure: ', error);
                    dispatch({ type: LOAD_REPORT_SPEC_FAIL, payload: LANGUAGES_CONTROLLER.format(null, 'dynamic_reports_generic_error') });
                });
        };
    },

    doLoadReportSpecificationV5: () => {
        return (dispatch) => {
            log.debug('doLoadReportSpecificationV5()');
            dispatch({ type: LOAD_REPORT_SPEC });
            return CLIENT.getReportSpecificationV5()
                .then((response) => {
                    log.debug('doLoadReportSpecificationV5(): success: ', response);
                    dispatch({ type: LOAD_REPORT_SPEC_OK, payload: response });
                })
                .catch((error) => {
                    log.debug('doLoadReportSpecificationV5(): failure: ', error);
                    dispatch({ type: LOAD_REPORT_SPEC_FAIL, payload: LANGUAGES_CONTROLLER.format(null, 'dynamic_reports_generic_error') });
                });
        };
    },

    doLoadLatestDynamicReportDefinitions: (userName, tenantId) => {
        return (dispatch) => {
            log.debug('doLatestDynamicReportDefinitions()');
            dispatch({ type: LOAD_REPORT_SPEC });
            return CLIENT.getLatestDynamicReportDefinitions(userName, tenantId)
                .then((response) => {
                    log.debug('doLatestDynamicReportDefinitions(): success: ', response);
                    dispatch({ type: LOAD_REPORT_SPEC_OK, payload: response });
                })
                .catch((error) => {
                    log.debug('doLatestDynamicReportDefinitions(): failure: ', error);
                    dispatch({ type: LOAD_REPORT_SPEC_FAIL, payload: LANGUAGES_CONTROLLER.format(null, 'dynamic_reports_generic_error') });
                });
        };
    },

    doLoadLatestDynamicReportData: (params) => {
        return (dispatch) => {
            log.debug('doLoadLatestDynamicReportData(): params: ', params);
            dispatch({ type: LOAD_REPORT_DATA });
            return CLIENT.getLatestDynamicReportsData(params)
                .then((response) => {
                    log.debug('doLoadLatestDynamicReportData(): success: ', response);
                    if (Array.isArray(response)) {
                        dispatch({ type: LOAD_REPORT_DATA_FAIL, payload: LANGUAGES_CONTROLLER.format(null, 'dynamic_reports_generic_error') });
                    } else {
                        dispatch({ type: LOAD_REPORT_DATA_OK, payload: response });
                    }
                })
                .catch((error) => {
                    log.debug('doLoadLatestDynamicReportData(): failure: ', error);
                    switch (error.status) {
                        case 400:
                            dispatch({ type: LOAD_REPORT_DATA_FAIL, payload: LANGUAGES_CONTROLLER.format(null, 'dynamic_reports_filter_criteria_error') });
                            break;
                        case 404:
                            dispatch({ type: LOAD_REPORT_DATA_FAIL, payload: LANGUAGES_CONTROLLER.format(null, 'dynamic_reports_nodatafound_error') });
                            break;
                        default:
                            dispatch({ type: LOAD_REPORT_DATA_FAIL, payload: LANGUAGES_CONTROLLER.format(null, 'dynamic_reports_generic_error') });
                    }
                });
        };
    },

    doLoadDynamicReportsData: (params, enabledAdditionalLogging) => {
        return (dispatch) => {
            log.debug('doLoadDynamicReportsData(): params: ', params);
            dispatch({ type: LOAD_REPORT_DATA });
            return CLIENT.getDynamicReportsData(params, enabledAdditionalLogging)
                .then((response) => {
                    log.debug('doLoadDynamicReportsData(): success: ', response);
                    if (Array.isArray(response)) {
                        dispatch({ type: LOAD_REPORT_DATA_FAIL, payload: LANGUAGES_CONTROLLER.format(null, 'dynamic_reports_generic_error') });
                    } else {
                        dispatch({ type: LOAD_REPORT_DATA_OK, payload: response });
                    }
                })
                .catch((error) => {
                    log.debug('doLoadDynamicReportsData(): failure: ', error);
                    switch (error.status) {
                        case 400:
                            dispatch({ type: LOAD_REPORT_DATA_FAIL, payload: LANGUAGES_CONTROLLER.format(null, 'dynamic_reports_filter_criteria_error') });
                            break;
                        case 404:
                            dispatch({ type: LOAD_REPORT_DATA_FAIL, payload: LANGUAGES_CONTROLLER.format(null, 'dynamic_reports_nodatafound_error') });
                            break;
                        default:
                            dispatch({ type: LOAD_REPORT_DATA_FAIL, payload: LANGUAGES_CONTROLLER.format(null, 'dynamic_reports_generic_error') });
                    }
                });
        };
    },

    doExportAllDynamicReportsToCSV: (params) => {
        return (dispatch) => {
            dispatch({ type: EXPORT_ALL });
            return CLIENT.exportAllDynamicReport(params)
                .then((response) => {
                    log.debug('doExportAllDynamicReportsToCSV success');
                    dispatch({ type: EXPORT_ALL_OK, payload: response});
                })
                .catch(() => {
                    log.debug('doExportAllDynamicReportsToCSV error');
                    dispatch({ type: EXPORT_ALL_FAIL, error: LANGUAGES_CONTROLLER.format(null, 'dynamic_reports_generic_error') });
                });
        }
    },
    doResetReportData: () => {
        return (dispatch) => {
            dispatch({ type: LOAD_REPORT_DATA_OK, payload: null });
        }
    },
    doHideToast: () => {
        return (dispatch) => {
            return new Promise((resolve) => {
                dispatch({ type: EXPORT_ALL });
                resolve();
            })
        }
    }
}

export {
    ACTION_TYPES,
    updateSubStateFromAction,
    API
}
